import { useRef } from "react";

interface PositionStyle {
  left?: number | string;
  right?: number | string;
  [key: string]: number | string | undefined;
}

interface UsePositionFilterReturn {
  getPosition: () => PositionStyle;
  popupRef: React.RefObject<HTMLDivElement | null>;
  buttonRef: React.RefObject<HTMLButtonElement | null>;
}

interface ContainerBounds {
  left: number;
  right: number;
}

/**
 * The horizontal band the popup has to stay inside: the nearest clipping or
 * scrolling ancestor, further clamped to the viewport. Drawn outside this band
 * the popup is either cut off by that ancestor or off screen.
 */
const getContainerBounds = (element: HTMLElement): ContainerBounds => {
  let parent = element.parentElement;
  while (parent) {
    const style = window.getComputedStyle(parent);
    const overflow = style.overflow + style.overflowX;
    if (
      overflow.includes("hidden") ||
      overflow.includes("auto") ||
      overflow.includes("scroll")
    ) {
      const rect = parent.getBoundingClientRect();
      return {
        left: Math.max(0, rect.left),
        right: Math.min(window.innerWidth, rect.right),
      };
    }
    parent = parent.parentElement;
  }
  return { left: 0, right: window.innerWidth };
};

const usePositionFilter = (): UsePositionFilterReturn => {
  const popupRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const getPosition = (): PositionStyle => {
    if (popupRef.current && buttonRef.current) {
      // Measure the popup where it actually lives, not as a clone in <body>.
      // The popup is shrink-to-fit inside a wrapper only as wide as the button,
      // so it renders at its min-content width; a clone parked at left: -9999px
      // has the whole viewport to grow into and reports max-content instead.
      // That over-reports by a wide margin and reads as "does not fit" on the
      // side where it does, which is what pushed wide panels off to the left.
      const popup = popupRef.current;
      const popupCss = popup.style.cssText;
      popup.style.visibility = "hidden";
      popup.style.display = "block";
      popup.style.left = "0px";
      popup.style.right = "auto";
      const { width: popupWidth } = popup.getBoundingClientRect();
      popup.style.cssText = popupCss;

      const { left: containerLeft, right: containerRight } = getContainerBounds(
        buttonRef.current
      );
      const { left: buttonLeft, right: buttonRight } =
        buttonRef.current.getBoundingClientRect();

      if (buttonLeft + popupWidth <= containerRight) {
        // opens below the button, left edges aligned
        return {
          left: 0,
        };
      } else if (buttonRight - popupWidth >= containerLeft) {
        // show popup to the left of the button
        return {
          left: "unset",
          right: 0,
        };
      } else {
        // Fits on neither side of the button — in a narrow container (the
        // "Configure filters" panes) flipping left would push the popup past
        // the container, cutting off its labels and first input. Sit flush
        // with the container's left edge instead. `left` resolves against the
        // popup's containing block, which is the button's offsetParent.
        const originLeft =
          buttonRef.current.offsetParent?.getBoundingClientRect().left ??
          buttonLeft;
        return {
          left: containerLeft - originLeft,
          right: "unset",
        };
      }
    }
    return {};
  };

  return { getPosition, popupRef, buttonRef };
};

export default usePositionFilter;
